import React, { ComponentProps, useEffect, useState } from "react";
import { AddressSearchMap } from "../AddressSearchMap/AddressSearchMap";
import { AddressForm } from "../AddressForm/AddressForm";
import { BackButtonEvent, IonModal, isPlatform, useIonAlert } from "@ionic/react";
import { useCustomerAddressesStateData } from "../../providers/CustomerAddresses/CustomerAddressesStateContext";
import CommonModal from "../UI/CommonModal/CommonModal";
import { YMaps } from "react-yandex-maps";

import classNames from "classnames/bind";
import styles from "./ShippingAddressManually.module.scss";
import { useGetShippingZonesQuery } from "../../providers/shippingAndPayment";
import { useAuth } from "../../contexts/auth-context";
import { useCustomerAddressesStorageData } from "../../providers/customerAddressesStorage";
import { Address, AddressInput } from "@core/types";
import { useCreateCustomerAddress, useUpdateCustomerAddress } from "../../providers/CustomerAddresses";
import { Keyboard } from "@capacitor/keyboard";
import { Capacitor } from "@capacitor/core";
import { useTranslation } from "react-i18next";

const cx = classNames.bind(styles);

interface ShippingAddressManuallyProps extends ComponentProps<typeof IonModal> {
  onNewAddressCreated?: (address: Address) => void;
  onDismiss: () => void;
}

const ShippingAddressManually = ({ onNewAddressCreated, onDismiss, ...modalProps }: ShippingAddressManuallyProps) => {
  const { isUserAuthenticated } = useAuth();
  const { user } = useAuth();
  const { t } = useTranslation();

  const { mutateAsync: updateCustomerAddress } = useUpdateCustomerAddress();
  const { mutateAsync: createCustomerAddress } = useCreateCustomerAddress();

  const { unAuthedCustomerAddresses, addAddress, setCurrentShippingAddressId } = useCustomerAddressesStorageData();

  const { customerAddress, setCustomerAddress, setIsAddressModalOpen } = useCustomerAddressesStateData();
  const { data: cities = [] } = useGetShippingZonesQuery();
  const [showAddressForm, setShowAddressForm] = useState(false);

  const [presentAlert] = useIonAlert();

  const onSubmit = (data: AddressInput) => {
    presentAlert({
      header: t("Please make sure that the entered address matches the one on the map"),
      cssClass: "custom-alert",
      backdropDismiss: false,
      buttons: [
        {
          text: t("Back to map"),
          handler: () => {
            setShowAddressForm(false);
          },
        },
        {
          text: t("Confirm"),
          cssClass: "alert-button-confirm",
          handler: async () => {
            const formData = {
              ...data,
              isFlat: !!data.apartmentNumber,
              latitude: customerAddress?.latitude || "",
              longitude: customerAddress?.longitude || "",
              state: data.city,
            };

            if (!isUserAuthenticated) {
              const unAuthedCustomerAddressId = String(unAuthedCustomerAddresses.length);
              const address = {
                ...formData,
                id: unAuthedCustomerAddressId,
              };
              addAddress(address);
              setCurrentShippingAddressId(unAuthedCustomerAddressId, "ShippingAddressManually");
              // updateOrderData({ address });
              onNewAddressCreated?.(address);
              setIsAddressModalOpen(false);
              setCustomerAddress(undefined);
              setShowAddressForm(false);
              return;
            }

            if (customerAddress?.id) {
              await updateCustomerAddress({
                address: { ...formData, id: customerAddress?.id },
                customerId: user?.id,
              });
            } else {
              const address = await createCustomerAddress({
                customerId: user?.id || "",
                customerAddressInput: formData,
              });
              if (address) {
                // updateOrderData({ address });
                onNewAddressCreated?.(address);
                setCurrentShippingAddressId(address.id + "", "ShippingAddressManually");
              }
            }
            setIsAddressModalOpen(false);
            setCustomerAddress(undefined);
            setShowAddressForm(false);
          },
        },
      ],
    });
  };

  const handleDismiss = () => {
    if (showAddressForm) {
      setShowAddressForm(false);
    } else {
      setIsAddressModalOpen(false);
      setCustomerAddress(undefined);
    }

    if (Capacitor.isNativePlatform()) {
      Keyboard.hide();
    }
  };

  useEffect(() => {
    if (isPlatform("mobile")) {
      const backHandler = (ev: Event) => {
        (ev as BackButtonEvent).detail.register(100, () => {
          handleDismiss();
        });
      };

      document.addEventListener("ionBackButton", backHandler);

      return () => {
        document.removeEventListener("ionBackButton", backHandler);
      };
    }
  }, [showAddressForm]);

  return (
    <IonModal {...modalProps} className={cx("addressModal")}>
      <CommonModal
        onDismiss={handleDismiss}
        title={t("Add a new shipping address")}
        classNameContent={cx("addressModalContent", showAddressForm ? "addressModalContent-scrollVisible" : "")}
      >
        {showAddressForm ? (
          <AddressForm onSubmit={onSubmit} cities={cities} />
        ) : (
          <AddressSearchMap setShowAddressForm={setShowAddressForm} />
        )}
      </CommonModal>
    </IonModal>
  );
};

export { ShippingAddressManually };
